import { useCallback, useRef, useState } from 'react';

/**
 * useEmployeeTaskScheduleModal
 * A hook for opening and closing the employee task schedule modal.
 *
 * @returns {{
 *   open: boolean,
 *   employeeId: number | null,
 *   openModal: (employeeId: number) => Promise<number | null>,
 *   close: () => void,
 *   confirm: (isoWeek: number) => void,
 * }}
 */
export function useEmployeeTaskScheduleModal() {
  const [open, setOpen] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const resolverRef = useRef(null);

  const openModal = useCallback((employeeId) => {
    if (resolverRef.current) return Promise.resolve(null);

    setEmployeeId(employeeId);
    setOpen(true);

    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  function resolveAndClear(value) {
    resolverRef.current?.(value);
    resolverRef.current = null;
  }

  const close = useCallback(() => {
    setOpen(false);
    setEmployeeId(null);
    resolveAndClear(null);
  }, []);

  const confirm = useCallback((isoWeek) => {
    setOpen(false);
    setEmployeeId(null);
    resolveAndClear(isoWeek);
  }, []);

  return {
    open,
    employeeId,
    openModal,
    close,
    confirm,
  };
}
